// WYNGX Login — redesigned 1 aug 2026 (owner-provided mockup).
//
// Magic-link only (no passwords / social) — the hero shell around it was
// rebuilt from scratch: golden wings mark, layered hero copy, prominent
// gold CTA and a subtle multi-hue wave at the bottom. Language picker
// lives at the top so first-time visitors on a shared device can switch
// before typing an email. Hidden tester bypass still triggers on ?demo=1.
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronRight, Loader2, Mail, Check, Copy, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/api";
import { useAuth } from "@/auth/AuthContext";
import LanguagePicker from "@/components/LanguagePicker";

export default function Login() {
    const { t } = useTranslation();
    const nav = useNavigate();
    const [search] = useSearchParams();
    const { user, loginAsDemo } = useAuth();

    const [email, setEmail] = useState("");
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [devLink, setDevLink] = useState(null);
    const [demoLoading, setDemoLoading] = useState(false);
    const demoMode = search.get("demo") === "1";

    useEffect(() => {
        if (user && user.id) nav("/dashboard", { replace: true });
    }, [user, nav]);

    const requestLink = async (e) => {
        e?.preventDefault?.();
        const v = email.trim().toLowerCase();
        if (!v || !v.includes("@")) {
            toast.error(t("login.voer_een_geldig_e_mailadres_in"));
            return;
        }
        setSending(true);
        try {
            const { data } = await api.post("/auth/magic/request", { email: v });
            setSent(true);
            setDevLink(data?.dev_link || null);
            if (data?.dev_link) {
                toast.message(t("login.dev_mode_link_verschijnt_op_het_scherm"),
                              { duration: 3500 });
            } else {
                toast.success(t("login.check_je_inbox_klik_op_de_link_om_in_te_"));
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Kon link niet versturen");
        } finally {
            setSending(false);
        }
    };

    const demo = async () => {
        setDemoLoading(true);
        try {
            const u = await loginAsDemo();
            toast.success(`Welkom terug, ${u.name || "tester"}`);
            nav("/dashboard", { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Demo login mislukt");
        } finally {
            setDemoLoading(false);
        }
    };

    return (
        <div className="min-h-[100dvh] w-full bg-black text-white relative overflow-hidden flex flex-col"
             data-testid="login-page">
            {/* --- Ambient background wash (subtle, off to sides so the wings
                    silhouette stays fully black behind the image PNG) --- */}
            <div className="pointer-events-none absolute inset-0 opacity-70">
                <div className="absolute top-[52%] left-[8%] w-[220px] h-[220px] rounded-full blur-3xl"
                     style={{ background: "radial-gradient(circle, rgba(255,214,10,0.14) 0%, transparent 65%)" }} />
                <div className="absolute top-[52%] right-[8%] w-[220px] h-[220px] rounded-full blur-3xl"
                     style={{ background: "radial-gradient(circle, rgba(139,92,246,0.10) 0%, transparent 65%)" }} />
            </div>

            {/* --- Language pill --- */}
            <div className="relative pt-[max(env(safe-area-inset-top),14px)] pb-2 flex justify-center">
                <LanguagePicker />
            </div>

            {/* --- Main scroll area --- */}
            <div className="relative flex-1 flex flex-col items-center px-6 pt-4 pb-8">
                {/* Wings mark */}
                <div className="relative mt-4 mb-3">
                    {/* Glow behind the wings — the PNG has proper alpha now,
                        so a plain drop-shadow on the img would work; keeping
                        the disc for a slightly warmer, larger halo. */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="w-[240px] h-[160px] rounded-[50%] blur-3xl"
                             style={{ background: "radial-gradient(ellipse, rgba(255,214,10,0.55) 0%, rgba(255,214,10,0.08) 60%, transparent 78%)" }} />
                    </div>
                    <img
                        src="/wing-mark-v2.png"
                        alt="WYNGX"
                        className="relative w-[260px] h-[200px] object-contain"
                        style={{ filter: "drop-shadow(0 0 22px rgba(255,214,10,0.35))" }}
                        data-testid="login-wings"
                    />
                </div>

                {/* Wordmark */}
                <h1 className="font-display font-bold tracking-[0.32em] text-3xl mt-1"
                    data-testid="login-wordmark">
                    <span className="text-white">WYNG</span>
                    <span style={{ color: "#FFD60A",
                                   textShadow: "0 0 24px rgba(255,214,10,0.55)" }}>X</span>
                </h1>

                {/* Hero copy */}
                <div className="mt-8 text-center">
                    <h2 className="font-display font-bold text-[28px] leading-[1.15]">
                        {t("login.hero_line_1")}<br />
                        <span style={{ color: "#FFD60A",
                                       textShadow: "0 0 22px rgba(255,214,10,0.35)" }}>
                            {t("login.hero_line_2_accent")}
                        </span>
                        {t("login.hero_line_2_suffix")}
                    </h2>
                    <p className="mt-2 text-[13.5px] text-white/60">
                        {t("login.hero_subtitle")}
                    </p>
                </div>

                {/* Form */}
                <div className="w-full max-w-sm mt-8">
                    {!sent && (
                        <form onSubmit={requestLink} data-testid="magic-form">
                            <label className="text-[11px] uppercase tracking-[0.14em] font-bold mb-2 block"
                                   style={{ color: "#FFD60A" }}>
                                {t("login.email_label")}
                            </label>
                            <div className="relative">
                                <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50" />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder={t("login.email_placeholder")}
                                    autoComplete="email"
                                    autoFocus
                                    inputMode="email"
                                    data-testid="magic-email"
                                    className="w-full h-[52px] bg-white/[0.04] border border-white/10
                                               rounded-2xl pl-11 pr-4 text-[15px] outline-none
                                               placeholder:text-white/40
                                               focus:border-[#FFD60A]/70 focus:bg-white/[0.06]
                                               transition"
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={sending || !email.trim()}
                                data-testid="magic-submit"
                                className="mt-4 w-full h-[54px] rounded-full font-bold text-[15px]
                                           text-black inline-flex items-center justify-center gap-1.5
                                           active:scale-[0.99] transition disabled:opacity-50
                                           shadow-[0_10px_40px_-8px_rgba(255,214,10,0.55)]"
                                style={{
                                    background:
                                      "linear-gradient(180deg, #FFE24C 0%, #F5B301 100%)",
                                }}
                            >
                                {sending ? <Loader2 size={17} className="animate-spin" />
                                         : <>{t("login.continue")} <ChevronRight size={18} /></>}
                            </button>

                            {/* OR divider */}
                            <div className="mt-6 flex items-center gap-3 text-[11px]
                                            uppercase tracking-[0.24em] text-white/40">
                                <div className="flex-1 h-px bg-white/10" />
                                {t("login.or_divider")}
                                <div className="flex-1 h-px bg-white/10" />
                            </div>

                            {/* Legal */}
                            <p className="mt-4 text-[11.5px] text-white/45 text-center leading-relaxed px-2">
                                {t("login.legal_prefix")}{" "}
                                <Link to="/terms" className="underline"
                                      style={{ color: "#FFD60A" }}
                                      data-testid="login-terms-link">
                                    {t("login.legal_terms")}
                                </Link>{" "}
                                {t("login.legal_and")}{" "}
                                <Link to="/privacy" className="underline"
                                      style={{ color: "#FFD60A" }}
                                      data-testid="login-privacy-link">
                                    {t("login.legal_privacy")}
                                </Link>.
                            </p>
                        </form>
                    )}

                    {sent && (
                        <div className="text-center" data-testid="magic-sent">
                            <div className="h-14 w-14 mx-auto rounded-full bg-[#32D74B]/15
                                            border border-[#32D74B]/30
                                            flex items-center justify-center mb-4">
                                <Check size={22} className="text-[#32D74B]" />
                            </div>
                            <h3 className="font-display text-xl font-semibold mb-2">
                                {t("login.check_je_inbox")}
                            </h3>
                            <p className="text-[13px] text-white/60 leading-relaxed">
                                {t("login.we_hebben_een_inloglink_naar")}<br />
                                <strong className="text-white">
                                    {email.toLowerCase().trim()}
                                </strong> gestuurd.<br />
                                {t("login.open_de_mail_en_tik_op_de_knop_om_binnen")}
                            </p>

                            {devLink && (
                                <div className="mt-5 rounded-2xl p-3 text-left"
                                     style={{
                                        background: "rgba(255,214,10,0.08)",
                                        border: "1px dashed rgba(255,214,10,0.32)"
                                     }}>
                                    <p className="text-[10px] uppercase tracking-wider font-bold"
                                       style={{ color: "#FFD60A" }}>
                                        {t("login.dev_link_geen_resend_key_actief")}
                                    </p>
                                    <a href={devLink}
                                       className="text-[11px] text-white/90 break-all block mt-1 underline">
                                        {devLink}
                                    </a>
                                    <button
                                        onClick={() => {
                                            navigator.clipboard?.writeText(devLink);
                                            toast.success(t("login.link_gekopieerd"));
                                        }}
                                        className="mt-2 text-[10px] flex items-center gap-1"
                                        style={{ color: "#FFD60A" }}
                                    >
                                        <Copy size={10} /> {t("login.kopieer")}
                                    </button>
                                </div>
                            )}

                            <button
                                onClick={() => { setSent(false); setDevLink(null); }}
                                data-testid="magic-resend"
                                className="mt-5 text-[12px] font-semibold"
                                style={{ color: "#FFD60A" }}
                            >
                                {t("login.andere_e_mail_gebruiken")}
                            </button>
                        </div>
                    )}

                    {demoMode && (
                        <div className="mt-8 pt-6 border-t border-white/10">
                            <p className="text-[10px] uppercase tracking-wider font-bold
                                          text-white/50 text-center mb-3">
                                {t("login.internal_tester")}
                            </p>
                            <button
                                onClick={demo}
                                disabled={demoLoading}
                                data-testid="demo-login-btn"
                                className="w-full rounded-2xl py-3 border font-semibold text-[13px]
                                           flex items-center justify-center gap-2
                                           active:scale-[0.99] transition"
                                style={{
                                    borderColor: "rgba(255,214,10,0.4)",
                                    color: "#FFD60A",
                                    background: "rgba(255,214,10,0.05)"
                                }}
                            >
                                {demoLoading
                                    ? <Loader2 size={14} className="animate-spin" />
                                    : <Sparkles size={14} />}
                                {t("login.demo_log_in_als_tester")}
                            </button>
                        </div>
                    )}
                </div>

                {/* Diagnostics escape hatch (unchanged) */}
                <button
                    type="button"
                    onClick={() => nav("/diagnostics")}
                    data-testid="login-diag-link"
                    className="mt-6 text-[10px] text-white/30 underline"
                >
                    {t("login.diagnostiek")}
                </button>
            </div>

            {/* --- Bottom multi-hue wave (decorative, non-interactive) --- */}
            <BottomWave />
        </div>
    );
}

/* Sine-based wave with gold / cyan / magenta stroke — matches the mockup
   without pulling any new dep. Pure SVG so it scales crisp on every DPR. */
function BottomWave() {
    return (
        <div className="pointer-events-none absolute left-0 right-0 bottom-0 h-[180px]">
            <svg viewBox="0 0 800 180" width="100%" height="100%"
                 preserveAspectRatio="none" aria-hidden>
                <defs>
                    <linearGradient id="wg-gold" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0" stopColor="#22D3EE" stopOpacity="0" />
                        <stop offset="0.15" stopColor="#22D3EE" stopOpacity="0.55" />
                        <stop offset="0.55" stopColor="#F5B301" stopOpacity="0.95" />
                        <stop offset="1" stopColor="#F5B301" stopOpacity="0" />
                    </linearGradient>
                    <linearGradient id="wg-magenta" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0" stopColor="#8B5CF6" stopOpacity="0" />
                        <stop offset="0.4" stopColor="#8B5CF6" stopOpacity="0.55" />
                        <stop offset="0.9" stopColor="#F5B301" stopOpacity="0.4" />
                        <stop offset="1" stopColor="#F5B301" stopOpacity="0" />
                    </linearGradient>
                </defs>
                <path d="M0 130 C 200 60, 380 200, 560 110 S 820 60, 900 120"
                      fill="none" stroke="url(#wg-gold)" strokeWidth="2.5"
                      strokeLinecap="round" />
                <path d="M-40 155 C 160 90, 340 220, 520 140 S 780 90, 880 150"
                      fill="none" stroke="url(#wg-magenta)" strokeWidth="1.8"
                      strokeLinecap="round" opacity="0.85" />
                <path d="M-20 165 C 180 120, 380 210, 580 160 S 820 130, 900 170"
                      fill="none" stroke="url(#wg-gold)" strokeWidth="1.2"
                      strokeLinecap="round" opacity="0.55" />
            </svg>
        </div>
    );
}
